import { singlePageProps } from '@lib/singlePageProps';
import {
  TemplateIcon,
  BookOpenIcon,
  ClipboardCheckIcon,
  LightningBoltIcon
} from '@heroicons/react/outline';
import { URLS } from '@lib';
import { BulletsLeft } from '@components/integrations';
import {
  Code,
  GetStarted,
  Header,
  Powerful,
  SmallQuote
} from '@components/solutions';
import { RocketIcon } from '@components';
import { PageProps } from '../../types/page-props';
import { ResourceCard } from '@components/common/resource-card';

const { redwoodDocs } = URLS;

const signIn = `import { SignIn } from '@clerk/nextjs';
  ...
    return (
      <>
        <BackgroundImage />
        <SignIn />
      </>
    );
  ...`;

const userProfile = `import { UserProfile } from '@clerk/nextjs';
  ...
    return (
      <>
        <CustomHeader />
        <UserProfile />
      </>
    );
  ...`;

export default function RedwoodPage(props: PageProps) {
  const { image, text, body } = props;
  const resources = [
    {
      title: 'Documentation',
      description: body.find(
        (text: Record<string, unknown>) => text.key === 'docsDescription'
      ),
      icon: BookOpenIcon
    },
    {
      title: 'Guide',
      description: body.find(
        (text: Record<string, unknown>) => text.key === 'blogDescription'
      ),
      icon: ClipboardCheckIcon
    },
    {
      title: 'Demo Repository',
      description: body.find(
        (text: Record<string, unknown>) => text.key === 'pluginDescription'
      ),
      icon: RocketIcon
    }
  ];

  return (
    <>
      <section className="mt-10">
        <Header
          title={text.headerTitle as string}
          boldTitle={text.headerBoldTitle as string}
          subheader={text.headerSubheader as string}
        />
      </section>

      <GetStarted doc={redwoodDocs} />

      <section className="mt-12" id="quote">
        <SmallQuote
          quote={
            <>
              Clerk setup and Dashboard DX are SilkySmooth&trade; and left a
              great impression. It’s feature-rich and includes MFA, but none of
              that gets in the way of usability. I have no hesitation
              recommending developers give it a go — it’s as simple as typing{' '}
              <code>redwood setup auth clerk</code>
            </>
          }
          name={text.quoteName as string}
          title={text.quoteTitle as string}
          image={image.quote}
        />
        <BulletsLeft
          {...props}
          imageWidth={550}
          icons={[TemplateIcon, LightningBoltIcon]}
        />

        <div className="relative py-10">
          <div className="mx-auto max-w-md px-4 text-center sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8">
            <h2 className="text-base font-semibold uppercase tracking-wider text-clerk-blue">
              Resources
            </h2>
            <h3 className="mt-2 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Get Started
            </h3>
            <p className="mx-auto mt-5 max-w-prose text-xl text-gray-500">
              {text.resourcesDescription as string}
            </p>
            <div className="mt-12">
              <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {resources.map(resource => {
                  return (
                    <ResourceCard resource={resource} key={resource.title} />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Code signIn={signIn} userProfile={userProfile} />

      <section className="mt-32 mb-16">
        <Powerful />
      </section>
    </>
  );
}

export async function getStaticProps({ preview = false }) {
  const slug = '/solutions/redwood';
  return singlePageProps({ preview, slug });
}
